import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Divider,
   Grid, TextField, Typography
} from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import FormDialog from '../../common/FormDialog';
import { reportCardAPI } from '../../../services/api';

const termLabel = (t) => t ? `${t.name} ${t.year || ''}`.trim() : '';
const studName  = (s) => s?.full_name || `${s?.last_name || ''} ${s?.first_name || ''}`.trim() || s?.registration_no || '';
const acYear    = (t) => Number(t?.academic_year || t?.year || new Date().getFullYear());

const SectionLabel = ({ children }) => (
  <Typography variant="caption" fontWeight={700} color="text.secondary"
    sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 0.5 }}>
    {children}
  </Typography>
);

const GeneralCommentFormDialog = ({
  open, onClose, onSubmit, editingRecord,
  formData, setFormData, saving, isMobile = false,
  students = [], terms = [], classStreams = [],
  mode = 'edit',   // 'ct' | 'ht' | 'edit'
  canCT = true, canHT = true,
}) => {
  const [ctTemplates,    setCtTemplates]    = useState([]);
  const [htTemplates,    setHtTemplates]    = useState([]);
  const [selectedCtTpl,  setSelectedCtTpl]  = useState(null);
  const [selectedHtTpl,  setSelectedHtTpl]  = useState(null);
  const [classFilter,    setClassFilter]    = useState(null);
  const [streamFilter,   setStreamFilter]   = useState(null);
  const [autoAssigning,  setAutoAssigning]  = useState('');

  const isEditMode = mode === 'edit';
  const showCT     = isEditMode ? canCT : mode === 'ct';
  const showHT     = isEditMode ? canHT : mode === 'ht';

  const dialogTitle = isEditMode
    ? 'Edit General Comment'
    : mode === 'ct' ? 'Add Class Teacher Comment' : 'Add Headteacher Comment';

  useEffect(() => {
    if (!open) {
      setSelectedCtTpl(null); setSelectedHtTpl(null);
      setClassFilter(null); setStreamFilter(null);
      return;
    }
    if (showCT) {
      reportCardAPI.getCommentTemplates({ comment_type: 'Class Teacher' })
        .then((res) => setCtTemplates(Array.isArray(res.data) ? res.data : res.data?.data ?? []))
        .catch(() => setCtTemplates([]));
    }
    if (showHT) {
      reportCardAPI.getCommentTemplates({ comment_type: 'Headteacher' })
        .then((res) => setHtTemplates(Array.isArray(res.data) ? res.data : res.data?.data ?? []))
        .catch(() => setHtTemplates([]));
    }
  }, [open]); // eslint-disable-line

  // pre-set class/stream when editing
  useEffect(() => {
    if (!open || !editingRecord?.student) return;
    const cs = classStreams.find((c) => c.id === editingRecord.student.class_stream_id);
    if (!cs) return;
    setClassFilter({ id: cs.class_id,  name: cs.name?.split('-')[0]?.trim() || '' });
    setStreamFilter({ id: cs.stream_id, name: cs.stream_name || '' });
  }, [open, editingRecord, classStreams]);

  const classes = useMemo(() => {
    const seen = new Map();
    classStreams.forEach((cs) => {
      if (cs.class_id && !seen.has(cs.class_id))
        seen.set(cs.class_id, { id: cs.class_id, name: cs.name?.split('-')[0]?.trim() || `Class ${cs.class_id}` });
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [classStreams]);

  const streams = useMemo(() => {
    const pool = classFilter ? classStreams.filter((cs) => cs.class_id === classFilter.id) : classStreams;
    const seen = new Map();
    pool.forEach((cs) => {
      if (cs.stream_id && !seen.has(cs.stream_id))
        seen.set(cs.stream_id, { id: cs.stream_id, name: cs.stream_name || `Stream ${cs.stream_id}` });
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [classStreams, classFilter]);

  const filteredStudents = useMemo(() => {
    if (!classFilter && !streamFilter) return students;
    const csIds = new Set(
      classStreams
        .filter((cs) => (!classFilter || cs.class_id === classFilter.id) && (!streamFilter || cs.stream_id === streamFilter.id))
        .map((cs) => cs.id)
    );
    return students.filter((s) => csIds.has(s.class_stream_id));
  }, [students, classStreams, classFilter, streamFilter]);

  const handleClassChange = (_, v) => {
    setClassFilter(v); setStreamFilter(null);
    if (formData.student) setFormData({ ...formData, student: null });
  };
  const handleStreamChange = (_, v) => {
    setStreamFilter(v);
    if (formData.student) setFormData({ ...formData, student: null });
  };

  const handleAutoAssign = async (field) => {
    if (!formData.student?.id || !formData.term?.id) return;
    setAutoAssigning(field);
    try {
      await reportCardAPI.bulkGenerateClassTeacherComments({
        term_id: formData.term.id,
        academic_year: acYear(formData.term),
        student_ids: [formData.student.id],
      });
      const res = await reportCardAPI.getGeneralCommentForStudent(formData.student.id, {
        term_id: formData.term.id,
        academic_year: acYear(formData.term),
      });
      const payload = res?.data?.data ?? res?.data ?? null;
      if (payload) {
        setFormData((prev) => ({
          ...prev,
          class_teacher_comment: field === 'ct' ? (payload.class_teacher_comment || prev.class_teacher_comment) : prev.class_teacher_comment,
          headteacher_comment:   field === 'ht' ? (payload.headteacher_comment   || prev.headteacher_comment)   : prev.headteacher_comment,
        }));
      }
    } catch { /* silent */ } finally {
      setAutoAssigning('');
    }
  };

  return (
    <FormDialog
      open={open} onClose={onClose} onSubmit={onSubmit}
      title={dialogTitle} saving={saving} isMobile={isMobile} maxWidth="md"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>

        {/* ── 1. Select Student ── */}
        <Box>
          <SectionLabel>1. Select Student</SectionLabel>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Autocomplete fullWidth options={classes} value={classFilter} onChange={handleClassChange}
                getOptionLabel={(c) => c.name} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Class" size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Autocomplete fullWidth options={streams} value={streamFilter} onChange={handleStreamChange}
                getOptionLabel={(s) => s.name} isOptionEqualToValue={(o, v) => o.id === v.id}
                disabled={streams.length === 0}
                renderInput={(p) => <TextField {...p} label="Stream" size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete fullWidth options={filteredStudents} value={formData.student}
                onChange={(_, v) => setFormData({ ...formData, student: v })}
                getOptionLabel={studName} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, s) => (
                  <Box component="li" {...props} key={s.id}>
                    <Box>
                      <Typography variant="body2">{studName(s)}</Typography>
                      {s.registration_no && <Typography variant="caption" color="text.secondary">{s.registration_no}</Typography>}
                    </Box>
                  </Box>
                )}
                renderInput={(p) => <TextField {...p} label="Student *" size="small"
                  helperText={!classFilter ? 'Select a class first to narrow the list' : `${filteredStudents.length} student${filteredStudents.length !== 1 ? 's' : ''} in this class/stream`}
                />}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* ── 2. Term ── */}
        <Box>
          <SectionLabel>2. Term</SectionLabel>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Autocomplete fullWidth options={terms} value={formData.term}
                onChange={(_, v) => setFormData({ ...formData, term: v })}
                getOptionLabel={termLabel} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, t) => (
                  <Box component="li" {...props} key={t.id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">{termLabel(t)}</Typography>
                      {t.is_current && <Chip label="Current" size="small" color="success" />}
                    </Box>
                  </Box>
                )}
                renderInput={(p) => <TextField {...p} label="Term *" size="small" />}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* ── 3. Comment(s) ── */}
        <Box>
          <SectionLabel>
            {isEditMode ? '3. Comments' : mode === 'ct' ? '3. Class Teacher Comment' : '3. Headteacher Comment'}
          </SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {showCT && (
              <Box>
                {isEditMode && (
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
                    <Typography variant="body2" fontWeight={600}>Class Teacher Comment</Typography>
                    <Button size="small"
                      startIcon={autoAssigning === 'ct' ? <CircularProgress size={14} /> : <AutoAwesome />}
                      onClick={() => handleAutoAssign('ct')}
                      disabled={!!autoAssigning || !formData.student?.id || !formData.term?.id}>
                      Auto-assign
                    </Button>
                  </Box>
                )}
                {!isEditMode && (
                  <Box display="flex" justifyContent="flex-end" mb={0.75}>
                    <Button size="small"
                      startIcon={autoAssigning === 'ct' ? <CircularProgress size={14} /> : <AutoAwesome />}
                      onClick={() => handleAutoAssign('ct')}
                      disabled={!!autoAssigning || !formData.student?.id || !formData.term?.id}>
                      Auto-assign
                    </Button>
                  </Box>
                )}
                {ctTemplates.length > 0 && (
                  <Autocomplete fullWidth options={ctTemplates} value={selectedCtTpl}
                    onChange={(_, t) => { setSelectedCtTpl(t); if (t) setFormData({ ...formData, class_teacher_comment: t.comment_text }); }}
                    getOptionLabel={(t) => t.template_code}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderInput={(p) => <TextField {...p} label="Pick from template (optional)" size="small" helperText="Fills the comment box below" />}
                    sx={{ mb: 1 }}
                  />
                )}
                <TextField fullWidth multiline rows={3}
                  label="Class Teacher Comment *"
                  value={formData.class_teacher_comment}
                  onChange={(e) => { setFormData({ ...formData, class_teacher_comment: e.target.value }); if (selectedCtTpl) setSelectedCtTpl(null); }}
                  placeholder="Class teacher's remarks..."
                />
                <Typography variant="caption" color="text.secondary" align="right" display="block" mt={0.5}>
                  {formData.class_teacher_comment.length} characters
                </Typography>
              </Box>
            )}

            {isEditMode && showCT && showHT && <Divider />}

            {showHT && (
              <Box>
                {isEditMode && (
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
                    <Typography variant="body2" fontWeight={600}>Headteacher Comment</Typography>
                    <Button size="small"
                      startIcon={autoAssigning === 'ht' ? <CircularProgress size={14} /> : <AutoAwesome />}
                      onClick={() => handleAutoAssign('ht')}
                      disabled={!!autoAssigning || !formData.student?.id || !formData.term?.id}>
                      Auto-assign
                    </Button>
                  </Box>
                )}
                {!isEditMode && (
                  <Box display="flex" justifyContent="flex-end" mb={0.75}>
                    <Button size="small"
                      startIcon={autoAssigning === 'ht' ? <CircularProgress size={14} /> : <AutoAwesome />}
                      onClick={() => handleAutoAssign('ht')}
                      disabled={!!autoAssigning || !formData.student?.id || !formData.term?.id}>
                      Auto-assign
                    </Button>
                  </Box>
                )}
                {htTemplates.length > 0 && (
                  <Autocomplete fullWidth options={htTemplates} value={selectedHtTpl}
                    onChange={(_, t) => { setSelectedHtTpl(t); if (t) setFormData({ ...formData, headteacher_comment: t.comment_text }); }}
                    getOptionLabel={(t) => t.template_code}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderInput={(p) => <TextField {...p} label="Pick from template (optional)" size="small" helperText="Fills the comment box below" />}
                    sx={{ mb: 1 }}
                  />
                )}
                <TextField fullWidth multiline rows={3}
                  label="Headteacher Comment *"
                  value={formData.headteacher_comment}
                  onChange={(e) => { setFormData({ ...formData, headteacher_comment: e.target.value }); if (selectedHtTpl) setSelectedHtTpl(null); }}
                  placeholder="Headteacher's remarks..."
                />
                <Typography variant="caption" color="text.secondary" align="right" display="block" mt={0.5}>
                  {formData.headteacher_comment.length} characters
                </Typography>
              </Box>
            )}

          </Box>
        </Box>

      </Box>
    </FormDialog>
  );
};

export default GeneralCommentFormDialog;
